/**
 * A ZIP archive with every entry stored, not compressed (GRO-81 design, D4).
 *
 * An `.xlsx` is a ZIP of a few XML parts, and a creatives table is small enough that
 * compression buys nothing worth a dependency. The writer follows APPNOTE.TXT: a local
 * header and the data per entry, then the central directory, then the end record. Every
 * entry carries the DOS epoch, 1980-01-01 00:00, so the same entries give the same bytes.
 */

export interface ZipEntry {
  name: string
  data: Uint8Array | string
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of data) crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

/** 1980-01-01 00:00 in MS-DOS date and time fields. */
const DOS_TIME = 0
const DOS_DATE = (0 << 9) | (1 << 5) | 1
/** Bit 11: names are UTF-8. */
const FLAGS = 0x0800
const VERSION = 20

export function zip(entries: readonly ZipEntry[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder()
  const files = entries.map((entry) => {
    const name = encoder.encode(entry.name)
    const data = typeof entry.data === 'string' ? encoder.encode(entry.data) : entry.data
    return { name, data, crc: crc32(data) }
  })

  const localSize = files.reduce((sum, file) => sum + 30 + file.name.length + file.data.length, 0)
  const centralSize = files.reduce((sum, file) => sum + 46 + file.name.length, 0)
  const out = new Uint8Array(localSize + centralSize + 22)
  const view = new DataView(out.buffer)
  let at = 0
  const u16 = (value: number) => { view.setUint16(at, value, true); at += 2 }
  const u32 = (value: number) => { view.setUint32(at, value, true); at += 4 }
  const bytes = (value: Uint8Array) => { out.set(value, at); at += value.length }

  const offsets: number[] = []
  for (const file of files) {
    offsets.push(at)
    u32(0x04034b50)
    u16(VERSION); u16(FLAGS); u16(0); u16(DOS_TIME); u16(DOS_DATE)
    u32(file.crc); u32(file.data.length); u32(file.data.length)
    u16(file.name.length); u16(0)
    bytes(file.name)
    bytes(file.data)
  }

  const centralStart = at
  files.forEach((file, index) => {
    u32(0x02014b50)
    u16(VERSION); u16(VERSION); u16(FLAGS); u16(0); u16(DOS_TIME); u16(DOS_DATE)
    u32(file.crc); u32(file.data.length); u32(file.data.length)
    u16(file.name.length); u16(0); u16(0); u16(0); u16(0); u32(0)
    u32(offsets[index])
    bytes(file.name)
  })

  const centralLength = at - centralStart
  u32(0x06054b50)
  u16(0); u16(0); u16(files.length); u16(files.length)
  u32(centralLength); u32(centralStart); u16(0)
  return out
}
